import type {
  AnnotationTemplate,
  Position,
} from '@/services/hooks/template_attributes'
import {
  DEFAULT_KONVA_TEXT_FONT_SIZE,
  DEFAULT_KONVA_TEXT_PADDING,
  LEGACY_BOUNDS_VISUAL_OFFSET,
  LEGACY_LINE_VISUAL_OFFSET,
  getAnnotationZ,
  getLegacyStrokeWidthForType,
  isKonvaAnnotationTemplate,
  KONVA_ANNOTATION_SCHEMA_VERSION,
  makeBoundsGeometry,
  makePointsGeometry,
  type KonvaAnnotationTemplate,
  type PersistedAnnotationTemplate,
} from './annotationSchema'
import { HARDWARE_ICON_SOURCES } from './annotationIconSources'
import {
  LEGACY_ARROW_TEXT_CORNER_RADIUS,
  LEGACY_ARROW_TEXT_HEIGHT,
  LEGACY_ARROW_TEXT_MAX_LENGTH,
  LEGACY_ARROW_TEXT_WIDTH,
  LEGACY_TRIANGLE_Y_OFFSET,
  getArrowTextPlacement,
  getLegacyBounds,
  getLegacyStroke,
} from './annotationPresets'
import {
  getAnnotationKindForLegacyType,
  isLegacyLineBasedAnnotationType,
} from './annotationMetadata'

const getPointDelta = ({
  startPoint,
  endPoint,
}: {
  startPoint: Position
  endPoint: Position
}) => ({
  dx: endPoint.x - startPoint.x,
  dy: endPoint.y - startPoint.y,
})

const mapLegacyLineLikeAnnotation = (
  annotation: AnnotationTemplate,
): KonvaAnnotationTemplate => {
  const startPoint = annotation.startPoint ??
    annotation.position ?? {
      x: 0,
      y: 0,
    }
  const endPoint = annotation.endPoint ?? startPoint
  const { dx, dy } = getPointDelta({ startPoint, endPoint })
  const width = Math.abs(dx)
  const height = Math.abs(dy)

  return {
    schema: KONVA_ANNOTATION_SCHEMA_VERSION,
    kind: getAnnotationKindForLegacyType(annotation.type),
    geometry: makePointsGeometry({
      x: startPoint.x - LEGACY_LINE_VISUAL_OFFSET.x,
      y: startPoint.y - LEGACY_LINE_VISUAL_OFFSET.y,
      points: [0, 0, dx, dy],
      width,
      height,
      z: getAnnotationZ(annotation.position),
    }),
    style: {
      stroke: getLegacyStroke(annotation.strokeColor),
      strokeWidth: getLegacyStrokeWidthForType({
        size: annotation.size ?? 'small',
        type: annotation.type,
      }),
      fill: null,
      opacity: 1,
    },
    text:
      annotation.text || annotation.type.includes('arrow-with-text')
        ? {
            enabled: true,
            value: annotation.text ?? '',
            align: 'center',
            verticalAlign: 'middle',
            placement: getArrowTextPlacement(annotation.type),
            padding: DEFAULT_KONVA_TEXT_PADDING,
            fontSize: DEFAULT_KONVA_TEXT_FONT_SIZE,
            color: getLegacyStroke(annotation.strokeColor),
            boxWidth: LEGACY_ARROW_TEXT_WIDTH,
            boxHeight: LEGACY_ARROW_TEXT_HEIGHT,
            boxCornerRadius: LEGACY_ARROW_TEXT_CORNER_RADIUS,
            maxLength: LEGACY_ARROW_TEXT_MAX_LENGTH,
          }
        : undefined,
    subPageId: annotation.subPageId,
    legacyType: annotation.type,
    bubbleValue: annotation.bubbleValue,
  }
}

const mapLegacyBoundsAnnotation = (
  annotation: AnnotationTemplate,
): KonvaAnnotationTemplate => {
  const { width, height, inset } = getLegacyBounds(annotation)
  const x =
    (annotation.position?.x ?? 0) + inset + LEGACY_BOUNDS_VISUAL_OFFSET.x
  const y =
    (annotation.position?.y ?? 0) +
    inset +
    LEGACY_BOUNDS_VISUAL_OFFSET.y +
    (annotation.type === 'triangle' ? LEGACY_TRIANGLE_Y_OFFSET : 0)

  const kind = getAnnotationKindForLegacyType(annotation.type)

  return {
    schema: KONVA_ANNOTATION_SCHEMA_VERSION,
    kind,
    geometry: makeBoundsGeometry({
      x,
      y,
      width,
      height,
      z: getAnnotationZ(annotation.position),
      cornerRadius: annotation.type === 'roundRect' ? 8 : undefined,
    }),
    style: {
      stroke: getLegacyStroke(annotation.strokeColor),
      strokeWidth: getLegacyStrokeWidthForType({
        size: annotation.size,
        type: annotation.type,
      }),
      fill: null,
      opacity: 1,
    },
    text: annotation.text
      ? {
          enabled: true,
          value: annotation.text,
          align: 'center',
          verticalAlign: 'middle',
          placement: 'inside',
          padding: DEFAULT_KONVA_TEXT_PADDING,
          fontSize: DEFAULT_KONVA_TEXT_FONT_SIZE,
          color: getLegacyStroke(annotation.strokeColor),
        }
      : undefined,
    subPageId: annotation.subPageId,
    legacyType: annotation.type,
    iconName: kind === 'icon' ? annotation.type : undefined,
    iconSrc:
      kind === 'icon' ? HARDWARE_ICON_SOURCES[annotation.type] : undefined,
  }
}

export const mapLegacyAnnotationToKonva = (
  annotation: AnnotationTemplate,
): KonvaAnnotationTemplate => {
  if (isLegacyLineBasedAnnotationType(annotation.type)) {
    return mapLegacyLineLikeAnnotation(annotation)
  }

  return mapLegacyBoundsAnnotation(annotation)
}

export const toRenderableKonvaAnnotation = (
  templateValues: PersistedAnnotationTemplate,
): KonvaAnnotationTemplate => {
  if (isKonvaAnnotationTemplate(templateValues)) {
    const positionZ = (
      templateValues as KonvaAnnotationTemplate & {
        position?: Position
      }
    ).position?.z

    if (
      templateValues.geometry.z === undefined &&
      typeof positionZ === 'number'
    ) {
      return {
        ...templateValues,
        geometry: {
          ...templateValues.geometry,
          z: positionZ,
        },
      }
    }

    return templateValues
  }

  return mapLegacyAnnotationToKonva(templateValues)
}
